import { noChange } from 'lit';
import { AsyncDirective } from 'lit/async-directive.js';
import { directive, type ElementPart } from 'lit/directive.js';

/** Largest rendered word fragment. Native language-aware hyphenation, when available, is respected. */
const widestWord = (heading: HTMLElement): number => {
  const walker = heading.ownerDocument.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
  const nodes: Array<{ node: Text; start: number; end: number }> = [];
  let text = '';
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    nodes.push({ node, start: text.length, end: text.length + node.length });
    text += node.data;
  }
  let widest = 0;
  for (const match of text.matchAll(/\S+/gu)) {
    const start = match.index;
    const end = start + match[0].length;
    const first = nodes.find(entry => entry.end > start);
    const last = nodes.find(entry => entry.end >= end);
    if (!first || !last) continue;
    const range = heading.ownerDocument.createRange();
    range.setStart(first.node, start - first.start);
    range.setEnd(last.node, end - last.start);
    for (const rect of range.getClientRects()) widest = Math.max(widest, rect.width);
  }
  // Highlight backgrounds need their inline padding as well as the word's glyphs.
  let padding = 0;
  for (const span of heading.querySelectorAll('.vp-heading-highlight')) {
    const style = heading.ownerDocument.defaultView!.getComputedStyle(span);
    padding = Math.max(padding, parseFloat(style.paddingLeft) + parseFloat(style.paddingRight));
  }
  return widest + padding;
};

const availableWidth = (heading: HTMLElement): number => {
  const view = heading.ownerDocument.defaultView!;
  let width = heading.clientWidth;
  for (let parent = heading.parentElement; parent; parent = parent.parentElement) {
    const style = view.getComputedStyle(parent);
    if (style.display !== 'inline' && parent.clientWidth > 0)
      width = Math.min(width, parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    if (parent.matches('.vp-page, .vp-editor-preview-page')) break;
  }
  const style = view.getComputedStyle(heading);
  return width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
};

const fitHeading = (heading: HTMLElement) => {
  heading.removeAttribute('data-vp-heading-fitted');
  heading.style.removeProperty('--vp-heading-fit-size');
  const view = heading.ownerDocument.defaultView;
  if (!view || !view.matchMedia('(max-width: 1023px)').matches || !heading.isConnected || heading.offsetHeight < 2)
    return;
  // At most two corrections on layout changes, never a running animation or polling loop.
  for (let attempt = 0; attempt < 2; attempt++) {
    const width = availableWidth(heading);
    const word = widestWord(heading);
    if (width <= 0 || word <= width + 0.5) break;
    const size = (parseFloat(view.getComputedStyle(heading).fontSize) * (width - 1)) / word;
    heading.style.setProperty('--vp-heading-fit-size', `${size}px`);
    heading.setAttribute('data-vp-heading-fitted', '');
  }
};

// Batch headings into one frame, including editor previews and orientation changes.
const pending = new Set<HTMLElement>();
let frame = 0;
const schedule = (heading: HTMLElement) => {
  pending.add(heading);
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    for (const node of pending) fitHeading(node);
    pending.clear();
  });
};

class HeadingWrappingDirective extends AsyncDirective {
  private heading?: HTMLElement;
  private resize?: ResizeObserver;
  private mutation?: MutationObserver;
  private fonts?: FontFaceSet;
  private refresh = () => {
    if (this.heading && this.isConnected) schedule(this.heading);
  };

  override render() {
    return noChange;
  }

  override update(part: ElementPart) {
    const node = part.element as HTMLElement;
    if (this.heading !== node) {
      this.resize?.disconnect();
      this.mutation?.disconnect();
      if (this.heading) pending.delete(this.heading);
      this.heading = node;
      this.observe();
      void node.ownerDocument.fonts.ready.then(() => {
        if (this.isConnected && this.heading) schedule(this.heading);
      });
    }
    schedule(node);
    return noChange;
  }

  private observe() {
    if (!this.heading) return;
    const view = this.heading.ownerDocument.defaultView;
    if (!view) return;
    this.resize ??= new view.ResizeObserver(() => {
      if (this.heading) schedule(this.heading);
    });
    this.resize.observe(this.heading);
    if (this.heading.parentElement) this.resize.observe(this.heading.parentElement);
    this.mutation ??= new view.MutationObserver(records => {
      // Editor text changes are applied directly to the DOM. Ignore only our own size variable.
      const styleWithoutFit = (value: string | null) =>
        (value ?? '')
          .split(';')
          .map(item => item.trim())
          .filter(item => item && !item.startsWith('--vp-heading-fit-size:'))
          .join(';');
      if (
        records.some(
          record =>
            record.type !== 'attributes' ||
            record.attributeName !== 'style' ||
            record.target !== this.heading ||
            styleWithoutFit(record.oldValue) !== styleWithoutFit(this.heading.getAttribute('style')),
        )
      )
        this.refresh();
    });
    this.mutation.observe(this.heading, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['style', 'class', 'lang', 'dir'],
    });
    this.fonts?.removeEventListener('loadingdone', this.refresh);
    this.fonts = this.heading.ownerDocument.fonts;
    this.fonts.addEventListener('loadingdone', this.refresh);
  }

  protected override disconnected() {
    this.resize?.disconnect();
    this.mutation?.disconnect();
    this.fonts?.removeEventListener('loadingdone', this.refresh);
    if (this.heading) pending.delete(this.heading);
  }

  protected override reconnected() {
    this.observe();
    if (this.heading) schedule(this.heading);
  }
}

export const headingWrapping = directive(HeadingWrappingDirective);
